
import { useState } from "react"
import { useAccessContext } from "../component/guard/AccessGuard"
import { api, firestore, storage } from "../core"
import { useToast } from "popopo-lib-front/hook"
import { useZodForm } from "popopo-lib-front/zod-form"
import { ManagedFireStorageUpload, FirebaseAudioPlayer } from "popopo-lib-front/component"
import {
  useMyVoices,
  useVoiceTimelines,
  useGlobalVoices,
  useVoiceReplies,
  useVoiceEmotes,
  useVoiceReplyEmotes,
  useUserFavorites,
  useSpaceBGMsForAdmin,
} from "popopo-lib-front/firestore-access"
import {
  UserId,
  VoiceId,
  VoiceReplyId,
  CreateVoiceRequest,
  CreateVoiceReplyRequest,
  FirebaseStoragePaths,
} from "popopo-msg"
import type { UserFavoriteDocument } from "popopo-msg"
import type {
  CreateVoiceRequest as CreateVoiceRequestType,
  CreateVoiceReplyRequest as CreateVoiceReplyRequestType,
  VoiceEmoteKind,
  VoiceEmoteTiming,
  VoiceReplyMixKind,
} from "popopo-msg"
import { Spinner } from "react-bootstrap"

const EMOTE_KINDS: { value: VoiceEmoteKind; label: string }[] = [
  { value: "thumbsup", label: "👍" },
  { value: "surprised", label: "😮" },
  { value: "laughter", label: "😂" },
  { value: "clapping", label: "👏" },
]

const EMOTE_TIMINGS: { value: VoiceEmoteTiming; label: string }[] = [
  { value: "playing", label: "再生中" },
  { value: "ending", label: "再生後" },
]

const MIX_KINDS: { value: VoiceReplyMixKind; label: string }[] = [
  { value: "always", label: "常に含める" },
  { value: "normal", label: "通常" },
  { value: "ignore", label: "除外" },
]

// ─── Voice Detail (Replies + Emotes) ─────────────────────────────────────────

const VoiceDetail = ({ voiceOwnerId, voiceId }: { voiceOwnerId: string; voiceId: string }) => {
  const context = useAccessContext()
  const toast = useToast()
  const replies = useVoiceReplies(firestore, UserId(voiceOwnerId), VoiceId(voiceId))
  const emotes = useVoiceEmotes(firestore, UserId(voiceOwnerId), VoiceId(voiceId))

  const replyForm = useZodForm({
    schema: CreateVoiceReplyRequest,
    mode: "onBlur",
    defaultValues: {
      audio_location: "",
      duration_ms: 0,
    },
  })
  const replyFormState = replyForm.formState

  const onCreateReply = async (values: CreateVoiceReplyRequestType) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.POST("/api/v2/users/{user_id}/voices/{voice_id}/voice-replies", {
        headers: { authorization: `Bearer ${idToken}` },
        params: { path: { user_id: voiceOwnerId, voice_id: voiceId } },
        body: { audio_location: values.audio_location, duration_ms: values.duration_ms },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Reply", message: "返信の作成に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Reply", message: "返信を作成しました" })
        replyForm.reset()
      }
    } catch {
      toast.add({ type: "danger", title: "Reply", message: "返信の作成に失敗しました" })
    }
  }

  const onDeleteReply = async (replyId: string) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.DELETE("/api/v2/users/{user_id}/voices/{voice_id}/voice-replies/{voice_reply_id}", {
        headers: { authorization: `Bearer ${idToken}` },
        params: { path: { user_id: voiceOwnerId, voice_id: voiceId, voice_reply_id: replyId } },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Reply Delete", message: "返信の削除に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Reply Delete", message: "返信を削除しました" })
      }
    } catch {
      toast.add({ type: "danger", title: "Reply Delete", message: "返信の削除に失敗しました" })
    }
  }

  const onSendEmote = async (kind: VoiceEmoteKind, timing: VoiceEmoteTiming) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.POST("/api/v2/users/{user_id}/voices/{voice_id}/voice-emotes", {
        headers: { authorization: `Bearer ${idToken}` },
        params: { path: { user_id: voiceOwnerId, voice_id: voiceId } },
        body: { kind, timing },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Emote", message: "エモートの送信に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Emote", message: `エモート ${kind} (${timing}) を送信しました` })
      }
    } catch {
      toast.add({ type: "danger", title: "Emote", message: "エモートの送信に失敗しました" })
    }
  }

  const onDeleteEmote = async (emoteId: string) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.DELETE("/api/v2/users/{user_id}/voices/{voice_id}/voice-emotes/{voice_emote_id}", {
        headers: { authorization: `Bearer ${idToken}` },
        params: { path: { user_id: voiceOwnerId, voice_id: voiceId, voice_emote_id: emoteId } },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Emote Delete", message: "エモートの削除に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Emote Delete", message: "エモートを削除しました" })
      }
    } catch {
      toast.add({ type: "danger", title: "Emote Delete", message: "エモートの削除に失敗しました" })
    }
  }

  const [selectedTiming, setSelectedTiming] = useState<VoiceEmoteTiming>("playing")

  return (
    <div className="mt-3 border-top pt-3">
      {/* エモート送信 */}
      <div className="mb-3">
        <strong className="small">エモート送信:</strong>
        <div className="d-flex gap-1 mt-1 align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={selectedTiming}
            onChange={(e) => setSelectedTiming(e.target.value as VoiceEmoteTiming)}
          >
            {EMOTE_TIMINGS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {EMOTE_KINDS.map((k) => (
            <button
              key={k.value}
              className="btn btn-outline-secondary btn-sm"
              onClick={() => onSendEmote(k.value, selectedTiming)}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {/* エモート一覧 */}
      {emotes !== "loading" && 0 < emotes.length && (
        <div className="mb-3">
          <strong className="small">エモート ({emotes.length}):</strong>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {emotes.map((emote) => (
              <span key={emote.id} className="badge bg-light text-dark border" style={{ cursor: "pointer" }}>
                {EMOTE_KINDS.find((k) => k.value === emote.kind)?.label ?? emote.kind}
                <small className="ms-1 text-muted">{emote.timing === "playing" ? "再生中" : "再生後"}</small>
                <small className="ms-1 text-muted">by {emote.user.name}</small>
                {emote.user.id === context.user.id && (
                  <button
                    className="btn-close btn-close-sm ms-1"
                    style={{ fontSize: "0.5rem" }}
                    onClick={() => onDeleteEmote(emote.id)}
                  />
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 返信作成フォーム */}
      <div className="mb-3">
        <strong className="small">返信を作成:</strong>
        <form className="d-flex flex-column gap-2 mt-1" onSubmit={replyForm.handleSubmit(onCreateReply)}>
          <ManagedFireStorageUpload
            storage={storage}
            control={replyForm.control}
            name="audio_location"
            label="返信音声ファイル"
            uploadPath={`/${FirebaseStoragePaths.VoiceReply.getAudioPathUploaded(context.credential.user.uid)}`}
            accept="audio/*"
            disabled={replyFormState.isSubmitting}
            resolveValue={(uploadPath) => uploadPath}
            trigger={replyForm.trigger}
            onUploaded={async (file) => {
              try {
                const audioContext = new AudioContext()
                const arrayBuffer = await file.arrayBuffer()
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
                const durationMs = Math.round(audioBuffer.duration * 1000)
                replyForm.setValue("duration_ms", durationMs as never, { shouldValidate: true })
                await audioContext.close()
              } catch {
                // ignore
              }
            }}
          />
          {0 < replyForm.watch("duration_ms") && (
            <p className="badge bg-info mb-0">音声の長さ: {replyForm.watch("duration_ms")} ms</p>
          )}
          {replyFormState.errors.duration_ms && (
            <div className="text-danger small">{replyFormState.errors.duration_ms.message}</div>
          )}
          <button
            type="submit"
            className="btn btn-outline-primary btn-sm"
            disabled={replyFormState.isSubmitting || !replyFormState.isValid}
          >
            返信を投稿
          </button>
        </form>
      </div>

      {/* 返信一覧 */}
      <div>
        <strong className="small">返信一覧:</strong>
        {replies === "loading" ? (
          <Spinner size="sm" />
        ) : replies.length === 0 ? (
          <p className="text-muted small">返信はありません</p>
        ) : (
          <div className="d-flex flex-column gap-2 mt-1">
            {replies.map((reply) => (
              <VoiceReplyCard
                key={reply.id}
                voiceOwnerId={voiceOwnerId}
                voiceId={voiceId}
                reply={reply}
                onDeleteReply={onDeleteReply}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Voice Reply Card (with Reply Emotes) ────────────────────────────────────

const VoiceReplyCard = ({
  voiceOwnerId,
  voiceId,
  reply,
  onDeleteReply,
}: {
  voiceOwnerId: string
  voiceId: string
  reply: { id: string; user: { id: string; name: string }; file: { location: string; duration_ms: number }; mix_kind: string; created_at: unknown }
  onDeleteReply: (replyId: string) => Promise<void>
}) => {
  const context = useAccessContext()
  const toast = useToast()
  const replyEmotes = useVoiceReplyEmotes(firestore, UserId(voiceOwnerId), VoiceId(voiceId), VoiceReplyId(reply.id))

  const onSendReplyEmote = async (kind: VoiceEmoteKind) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.POST("/api/v2/users/{user_id}/voices/{voice_id}/voice-replies/{voice_reply_id}/voice-reply-emotes", {
        headers: { authorization: `Bearer ${idToken}` },
        params: { path: { user_id: voiceOwnerId, voice_id: voiceId, voice_reply_id: reply.id } },
        body: { kind },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Reply Emote", message: "エモートの送信に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Reply Emote", message: `エモート ${kind} を送信しました` })
      }
    } catch {
      toast.add({ type: "danger", title: "Reply Emote", message: "エモートの送信に失敗しました" })
    }
  }

  const onUpdateMixKind = async (mixKind: VoiceReplyMixKind) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.PUT("/api/v2/users/{user_id}/voices/{voice_id}/voice-replies/{voice_reply_id}/mix-kind", {
        headers: { authorization: `Bearer ${idToken}` },
        params: { path: { user_id: voiceOwnerId, voice_id: voiceId, voice_reply_id: reply.id } },
        body: { mix_kind: mixKind },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Mix Kind", message: "mix_kind の更新に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Mix Kind", message: `mix_kind を ${mixKind} に更新しました` })
      }
    } catch {
      toast.add({ type: "danger", title: "Mix Kind", message: "mix_kind の更新に失敗しました" })
    }
  }

  const onDeleteReplyEmote = async (emoteId: string) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.DELETE("/api/v2/users/{user_id}/voices/{voice_id}/voice-replies/{voice_reply_id}/voice-reply-emotes/{voice_reply_emote_id}", {
        headers: { authorization: `Bearer ${idToken}` },
        params: { path: { user_id: voiceOwnerId, voice_id: voiceId, voice_reply_id: reply.id, voice_reply_emote_id: emoteId } },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Reply Emote Delete", message: "エモートの削除に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Reply Emote Delete", message: "エモートを削除しました" })
      }
    } catch {
      toast.add({ type: "danger", title: "Reply Emote Delete", message: "エモートの削除に失敗しました" })
    }
  }

  return (
    <div className="card bg-light">
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <small className="text-muted">
              {reply.user.name} ({reply.user.id}) — {reply.file.duration_ms}ms
            </small>
            {context.user.id === voiceOwnerId && (
              <select
                className="form-select form-select-sm ms-2"
                style={{ width: "auto", fontSize: "0.75rem" }}
                value={reply.mix_kind}
                onChange={(e) => onUpdateMixKind(e.target.value as VoiceReplyMixKind)}
              >
                {MIX_KINDS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            )}
            {context.user.id !== voiceOwnerId && (
              <small className="text-muted ms-2">mix: {reply.mix_kind}</small>
            )}
          </div>
          {reply.user.id === context.user.id && (
            <button className="btn btn-outline-danger btn-sm" onClick={() => onDeleteReply(reply.id)}>
              <i className="fas fa-trash" />
            </button>
          )}
        </div>
        <FirebaseAudioPlayer storage={storage} path={reply.file.location} />

        {/* Reply Emote 送信 */}
        <div className="d-flex gap-1 mt-1">
          {EMOTE_KINDS.map((k) => (
            <button
              key={k.value}
              className="btn btn-outline-secondary"
              style={{ fontSize: "0.7rem", padding: "1px 6px" }}
              onClick={() => onSendReplyEmote(k.value)}
            >
              {k.label}
            </button>
          ))}
        </div>

        {/* Reply Emote 一覧 */}
        {replyEmotes !== "loading" && 0 < replyEmotes.length && (
          <div className="d-flex flex-wrap gap-1 mt-1">
            {replyEmotes.map((emote) => (
              <span key={emote.id} className="badge bg-white text-dark border" style={{ fontSize: "0.7rem" }}>
                {EMOTE_KINDS.find((k) => k.value === emote.kind)?.label ?? emote.kind}
                <small className="ms-1 text-muted">by {emote.user.name}</small>
                {emote.user.id === context.user.id && (
                  <button
                    className="btn-close btn-close-sm ms-1"
                    style={{ fontSize: "0.4rem" }}
                    onClick={() => onDeleteReplyEmote(emote.id)}
                  />
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Voice Card ──────────────────────────────────────────────────────────────

const VoiceCard = ({
  voiceOwnerId,
  voiceId,
  title,
  fileLocation,
  durationMs,
  replyCount,
  power,
  createdAt,
  userName,
  bgmId,
  showDelete,
  onDelete,
  isFavorited,
  onToggleFavorite,
}: {
  voiceOwnerId: string
  voiceId: string
  title: string
  fileLocation: string
  durationMs: number
  replyCount: number
  power: number
  createdAt: unknown
  userName?: string
  bgmId?: string
  showDelete?: boolean
  onDelete?: () => void
  isFavorited?: boolean
  onToggleFavorite?: () => void
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="card">
      <div className="card-body">
        <h6 className="card-title">{title}</h6>
        <p className="card-text">
          <small className="text-muted">
            {userName && <>投稿者: {userName}<br /></>}
            ID: {voiceId}<br />
            長さ: {durationMs}ms / 返信数: {replyCount} / パワー: {power}<br />
            {bgmId && <>BGM: {bgmId}<br /></>}
            作成日時: {new Date(createdAt as string).toLocaleString()}
          </small>
        </p>
        <FirebaseAudioPlayer storage={storage} path={fileLocation} />
        <div className="d-flex gap-2 mt-2">
          {showDelete && onDelete && (
            <button className="btn btn-danger btn-sm" onClick={onDelete}>
              <i className="fas fa-trash me-1" />削除
            </button>
          )}
          {onToggleFavorite && (
            <button
              className={`btn btn-sm ${isFavorited ? "btn-warning" : "btn-outline-warning"}`}
              onClick={onToggleFavorite}
            >
              <i className={`fas fa-star me-1`} />
              {isFavorited ? "お気に入り解除" : "お気に入り"}
            </button>
          )}
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setExpanded(!expanded)}
          >
            <i className={`fas fa-${expanded ? "chevron-up" : "chevron-down"} me-1`} />
            {expanded ? "閉じる" : "返信・エモート"}
          </button>
        </div>
        {expanded && <VoiceDetail voiceOwnerId={voiceOwnerId} voiceId={voiceId} />}
      </div>
    </div>
  )
}

// ─── Favorite Entry ──────────────────────────────────────────────────────────

const FavoriteEntry = ({ favorite, onRemove }: { favorite: UserFavoriteDocument; onRemove: () => void }) => {
  return (
    <div className="card">
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <span className="badge bg-secondary me-2">{favorite.target.kind}</span>
            <small className="text-muted">
              owner: {favorite.target.voice_owner_user_id} / voice: {favorite.target.voice_id}
              {favorite.target.kind === "voice-reply" && <> / reply: {favorite.target.voice_reply_id}</>}
            </small>
            <br />
            <small className="text-muted">
              追加日時: {new Date(favorite.created_at as number).toLocaleString()}
            </small>
          </div>
          <button className="btn btn-outline-danger btn-sm" onClick={onRemove}>
            <i className="fas fa-star me-1" />解除
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export const Voices = () => {
  const context = useAccessContext()
  const toast = useToast()
  const myVoices = useMyVoices(firestore, UserId(context.user.id))
  const voiceTimelines = useVoiceTimelines(firestore, UserId(context.user.id))
  const globalVoices = useGlobalVoices(firestore)
  const userFavorites = useUserFavorites(firestore, UserId(context.user.id))
  const spaceBGMs = useSpaceBGMsForAdmin(firestore)

  const createForm = useZodForm({
    schema: CreateVoiceRequest,
    mode: "onBlur",
    defaultValues: {
      title: "",
      audio_location: "",
      duration_ms: 0,
    },
  })
  const { isSubmitting, isValid, errors } = createForm.formState

  const isVoiceFavorited = (voiceOwnerId: string, voiceId: string): boolean => {
    if (userFavorites === "loading") return false
    return userFavorites.some(
      (fav) => fav.target.kind === "voice" && fav.target.voice_owner_user_id === voiceOwnerId && fav.target.voice_id === voiceId
    )
  }

  const onAddFavorite = async (voiceOwnerId: string, voiceId: string) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.POST("/api/v2/users/me/favorites", {
        headers: { authorization: `Bearer ${idToken}` },
        body: { kind: "voice", voice_owner_user_id: voiceOwnerId, voice_id: voiceId },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Favorite", message: "お気に入り追加に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Favorite", message: "お気に入りに追加しました" })
      }
    } catch {
      toast.add({ type: "danger", title: "Favorite", message: "お気に入り追加に失敗しました" })
    }
  }

  const onRemoveFavorite = async (voiceOwnerId: string, voiceId: string) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.DELETE("/api/v2/users/me/favorites", {
        headers: { authorization: `Bearer ${idToken}` },
        body: { kind: "voice", voice_owner_user_id: voiceOwnerId, voice_id: voiceId },
      })
      if (result.error) {
        toast.add({ type: "danger", title: "Favorite", message: "お気に入り解除に失敗しました" })
      } else {
        toast.add({ type: "success", title: "Favorite", message: "お気に入りを解除しました" })
      }
    } catch {
      toast.add({ type: "danger", title: "Favorite", message: "お気に入り解除に失敗しました" })
    }
  }

  const onToggleFavorite = async (voiceOwnerId: string, voiceId: string) => {
    if (isVoiceFavorited(voiceOwnerId, voiceId)) {
      await onRemoveFavorite(voiceOwnerId, voiceId)
    } else {
      await onAddFavorite(voiceOwnerId, voiceId)
    }
  }

  const onCreateVoice = async (values: CreateVoiceRequestType) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.POST("/api/v2/users/me/voices", {
        headers: { authorization: `Bearer ${idToken}` },
        body: {
          title: values.title,
          audio_location: values.audio_location,
          duration_ms: values.duration_ms,
          bgm_id: values.bgm_id || undefined,
        },
      })

      if (result.error) {
        toast.add({ type: "danger", title: "Voice Create", message: result.error.message || "エラーが発生しました" })
      } else {
        toast.add({ type: "success", title: "Voice Create", message: "ボイスを作成しました" })
        createForm.reset()
      }
    } catch {
      toast.add({ type: "danger", title: "Voice Create", message: "ボイスの作成に失敗しました" })
    }
  }

  const onDeleteVoice = async (voiceId: string) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.DELETE("/api/v2/users/me/voices/{voice_id}", {
        params: { path: { voice_id: voiceId } },
        headers: { authorization: `Bearer ${idToken}` },
      })

      if (result.error) {
        toast.add({ type: "danger", title: "Voice Delete", message: result.error.message || "エラーが発生しました" })
      } else {
        toast.add({ type: "success", title: "Voice Delete", message: "ボイスを削除しました" })
      }
    } catch {
      toast.add({ type: "danger", title: "Voice Delete", message: "ボイスの削除に失敗しました" })
    }
  }

  return (
    <div className="row gy-5 justify-content-center">
      {/* ボイス作成フォーム */}
      <div className="d-flex col-12 col-xl-6 flex-column gap-3 align-items-center">
        <h3>ボイス作成</h3>
        <form className="d-flex w-100 flex-column gap-3" onSubmit={createForm.handleSubmit(onCreateVoice)}>
          <div className="form-floating">
            <input
              type="text"
              className="form-control"
              placeholder="タイトル"
              {...createForm.register("title")}
              disabled={isSubmitting}
            />
            <label>タイトル</label>
            {errors.title && (
              <div className="text-danger small mt-1">{errors.title.message}</div>
            )}
          </div>

          <ManagedFireStorageUpload
            storage={storage}
            control={createForm.control}
            name="audio_location"
            label="音声ファイル"
            uploadPath={`/${FirebaseStoragePaths.Voice.getAudioPathUploaded(context.credential.user.uid)}`}
            accept="audio/*"
            disabled={isSubmitting}
            resolveValue={(uploadPath) => uploadPath}
            trigger={createForm.trigger}
            onUploaded={async (file) => {
              try {
                const audioContext = new AudioContext()
                const arrayBuffer = await file.arrayBuffer()
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
                const durationMs = Math.round(audioBuffer.duration * 1000)
                createForm.setValue("duration_ms", durationMs as never, { shouldValidate: true })
                await audioContext.close()
              } catch {
                // ignore
              }
            }}
          />

          {0 < createForm.watch("duration_ms") && (
            <p className="badge bg-info">音声の長さ: {createForm.watch("duration_ms")} ms</p>
          )}
          {errors.duration_ms && (
            <div className="text-danger small">{errors.duration_ms.message}</div>
          )}

          <div className="form-floating">
            <select
              className="form-select"
              {...createForm.register("bgm_id")}
              disabled={isSubmitting || spaceBGMs === "loading"}
            >
              <option value="">BGMなし</option>
              {spaceBGMs !== "loading" && spaceBGMs.map((bgm) => (
                <option key={bgm.id} value={bgm.id}>{bgm.name}</option>
              ))}
            </select>
            <label>BGM（任意）</label>
          </div>

          <button
            type="submit"
            className="btn btn-primary form-control"
            disabled={isSubmitting || !isValid}
          >
            <i className="fas fa-microphone me-2" />
            ボイスを投稿
          </button>
        </form>

        {/* 自分のボイス一覧 */}
        <h3 className="mt-4">自分のボイス</h3>
        {myVoices === "loading" ? (
          <Spinner />
        ) : myVoices.length === 0 ? (
          <p>ボイスはありません</p>
        ) : (
          <div className="d-flex w-100 flex-column gap-3">
            {myVoices.map((voice) => (
              <VoiceCard
                key={voice.id}
                voiceOwnerId={context.user.id}
                voiceId={voice.id}
                title={voice.title}
                fileLocation={voice.file.location}
                durationMs={voice.file.duration_ms}
                replyCount={voice.counts.voice_reply_count}
                power={voice.counts.power}
                createdAt={voice.created_at}
                bgmId={voice.bgm_id}
                showDelete
                onDelete={() => onDeleteVoice(voice.id)}
                isFavorited={isVoiceFavorited(context.user.id, voice.id)}
                onToggleFavorite={() => onToggleFavorite(context.user.id, voice.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* タイムライン */}
      <div className="d-flex col-12 col-xl-6 flex-column gap-3 align-items-center">
        {/* お気に入り一覧 */}
        <h3>お気に入り</h3>
        {userFavorites === "loading" ? (
          <Spinner />
        ) : userFavorites.length === 0 ? (
          <p>お気に入りはありません</p>
        ) : (
          <div className="d-flex w-100 flex-column gap-3">
            {userFavorites.map((fav) => (
              <FavoriteEntry key={fav.id} favorite={fav} onRemove={() => {
                if (fav.target.kind === "voice") {
                  void onRemoveFavorite(fav.target.voice_owner_user_id, fav.target.voice_id)
                } else if (fav.target.kind === "voice-reply") {
                  void onRemoveFavorite(fav.target.voice_owner_user_id, fav.target.voice_id)
                }
              }} />
            ))}
          </div>
        )}

        {/* 自分のタイムライン */}
        <h3 className="mt-4">自分のタイムライン</h3>
        {voiceTimelines === "loading" ? (
          <Spinner />
        ) : voiceTimelines.length === 0 ? (
          <p>タイムラインにボイスはありません</p>
        ) : (
          <div className="d-flex w-100 flex-column gap-3">
            {voiceTimelines.map((entry) => (
              <VoiceCard
                key={entry.id}
                voiceOwnerId={entry.voice.user.id}
                voiceId={entry.voice.id}
                title={entry.voice.title}
                fileLocation={entry.voice.file.location}
                durationMs={entry.voice.file.duration_ms}
                replyCount={entry.voice.counts.voice_reply_count}
                power={entry.voice.counts.power}
                createdAt={entry.created_at}
                userName={entry.voice.user.name}
                bgmId={entry.voice.bgm_id}
                isFavorited={isVoiceFavorited(entry.voice.user.id, entry.voice.id)}
                onToggleFavorite={() => onToggleFavorite(entry.voice.user.id, entry.voice.id)}
              />
            ))}
          </div>
        )}

        {/* グローバルタイムライン */}
        <h3 className="mt-4">グローバルタイムライン</h3>
        {globalVoices === "loading" ? (
          <Spinner />
        ) : globalVoices.length === 0 ? (
          <p>グローバルにボイスはありません</p>
        ) : (
          <div className="d-flex w-100 flex-column gap-3">
            {globalVoices.map((voice) => (
              <VoiceCard
                key={voice.id}
                voiceOwnerId={voice.user.id}
                voiceId={voice.id}
                title={voice.title}
                fileLocation={voice.file.location}
                durationMs={voice.file.duration_ms}
                replyCount={voice.counts.voice_reply_count}
                power={voice.counts.power}
                createdAt={voice.created_at}
                userName={voice.user.name}
                bgmId={voice.bgm_id}
                isFavorited={isVoiceFavorited(voice.user.id, voice.id)}
                onToggleFavorite={() => onToggleFavorite(voice.user.id, voice.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
